import { useCallback } from 'react';

/**
 * A generic hook to determine if a specific field in a form card is "dirty" (modified).
 * @param isNewCard - If true, any truthy value is considered dirty.
 * @param originalData - The snapshot of the data before editing.
 * @param booleanFields - A list of keys that should always be treated as booleans (comparing truthiness).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useDirtyFieldCheck = <T extends Record<string, any>>(
	isNewCard: boolean,
	originalData: T | undefined | null,
	booleanFields: Array<keyof T> = []
) => {
	// We use a stringified version of booleanFields for the dependency array
	// to prevent infinite re-creation if the user passes an inline array literal.
	const booleanFieldsKey = booleanFields.join(',');

	return useCallback(
		(fieldName: keyof T, currentValue: unknown): boolean => {
			// Scenario A: New Card
			// If it's new, any value present (truthy) is considered dirty
			if (isNewCard) {
				return !!currentValue;
			}

			// Scenario B: Existing Card
			const originalVal = originalData?.[fieldName];

			const normalizedCurrent = currentValue ?? '';
			const normalizedOriginal = originalVal ?? '';

			// Check if we should force boolean comparison logic
			const shouldCheckAsBoolean =
				typeof normalizedOriginal === 'boolean' ||
				booleanFields.includes(fieldName);

			if (shouldCheckAsBoolean) {
				return !!normalizedCurrent !== !!normalizedOriginal;
			}

			return normalizedCurrent !== normalizedOriginal;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isNewCard, originalData, booleanFieldsKey]
	);
};
